import { BehaviorSubject, Observable } from 'rxjs';

export interface CommittedWord {
  word: string;
  index: number;
}

/**
 * Tracks per-message state: raw words from backend, committed (displayed) words,
 * and lifecycle flags (final, released, frozen). Emits displayed text as words are committed.
 */
export class MessageState {
  readonly documentId: string;

  private _rawWords: string[] = [];
  private _committedWords: CommittedWord[] = [];
  private _isFinal = false;
  private _frozenAt: number | null = null;
  private _released = false;
  private _staleResetCount = 0;

  private _displayedText$ = new BehaviorSubject<string>('');
  private _isTyping$ = new BehaviorSubject<boolean>(false);

  constructor(documentId: string) {
    this.documentId = documentId;
  }

  get rawWords(): string[] {
    return this._rawWords;
  }
  get committedCount(): number {
    return this._committedWords.length;
  }
  get isFinal(): boolean {
    return this._isFinal;
  }
  get isFrozen(): boolean {
    return this._frozenAt !== null;
  }
  get isReleased(): boolean {
    return this._released;
  }
  get displayedText$(): Observable<string> {
    return this._displayedText$.asObservable();
  }
  get isTyping$(): Observable<boolean> {
    return this._isTyping$.asObservable();
  }
  get displayedText(): string {
    return this._displayedText$.value;
  }
  get isTyping(): boolean {
    return this._isTyping$.value;
  }

  /**
   * Update raw words from backend. Silently updates committed words whose text
   * changed at the same index to prevent visual shifting.
   * Truncates committed words if raw words shrink.
   */
  updateRawWords(words: string[]): 'frozen' | 'ok' {
    if (this._frozenAt !== null) return 'frozen';

    // Growth-only: ignore a refinement that has fewer words than we have already
    // committed (displayed). Dropping committed words would make the on-screen
    // text shrink and then re-grow — visible flicker on the translation path.
    if (words.length < this._committedWords.length) {
      this._staleResetCount++;
      this._rawWords = [...words];
      return 'ok';
    }

    this._rawWords = [...words];

    let changed = false;
    for (const cw of this._committedWords) {
      if (cw.index < words.length && cw.word !== words[cw.index]) {
        cw.word = words[cw.index];
        changed = true;
      }
    }

    if (changed) this.emitDisplayedText();
    return 'ok';
  }

  markFinal(): void {
    this._isFinal = true;
  }
  markReleased(): void {
    this._released = true;
  }

  freeze(): void {
    if (this._frozenAt === null) this._frozenAt = Date.now();
  }

  /** Commits a word at the given index. Rejects duplicates, out-of-bounds, and stale words. */
  commitWord(index: number, word: string): void {
    if (!word || word.trim() === '') return;
    if (index >= this._rawWords.length) return;
    if (word !== this._rawWords[index]) return;
    if (this._committedWords.some(cw => cw.index === index)) return;

    this._committedWords.push({ word, index });
    this._committedWords.sort((a, b) => a.index - b.index);
    this.emitDisplayedText();
  }

  setTyping(isTyping: boolean): void {
    if (this._isTyping$.value !== isTyping) this._isTyping$.next(isTyping);
  }

  getUncommittedWords(): Array<{ index: number; word: string }> {
    const uncommitted: Array<{ index: number; word: string }> = [];
    for (let i = 0; i < this._rawWords.length; i++) {
      if (!this._committedWords.some(cw => cw.index === i)) {
        uncommitted.push({ index: i, word: this._rawWords[i] });
      }
    }
    return uncommitted;
  }

  isFullyCommitted(): boolean {
    return this._committedWords.length >= this._rawWords.length;
  }

  destroy(): void {
    this._displayedText$.complete();
    this._isTyping$.complete();
  }

  private emitDisplayedText(): void {
    this._displayedText$.next(this._committedWords.map(cw => cw.word).join(' '));
  }

  toDebugObject(): object {
    return {
      documentId: this.documentId,
      rawWords: [...this._rawWords],
      committedWords: this._committedWords.map(cw => ({ ...cw })),
      committedCount: this._committedWords.length,
      rawCount: this._rawWords.length,
      isFinal: this._isFinal,
      frozenAt: this._frozenAt,
      isReleased: this._released,
      displayedText: this.displayedText,
      staleResetCount: this._staleResetCount
    };
  }
}
